import React, { useEffect, useMemo, useRef } from 'react'
import { Box, Stack, Table, TableBody, TableContainer, TableRow, Tooltip } from '@mui/material'
import dayjs from 'dayjs'
import WeeklyCalendarHeader from './WeeklyCalendarHeader'
import {
  StyledHourCell,
  StyledLeaderTableCell,
  StyledStickyAvatarCell,
  StyledStickyHourCell,
} from '../../../styles/LeaderCalendarTableStyles'
import { Mentee } from '../../../store/types/response/LeaderMenteesPlanResponse'
import AvatarComponent from '../../avatar/AvatarComponent'
import { WeeklyBar } from './WeeklyBar'
import { useNavigate } from 'react-router-dom'

type WeeklyCalendarTableProps = {
  selectedDate: string
  mentees: Mentee[]
  earliestHour: number
}

export const WeeklyCalendarTable = ({ selectedDate, mentees, earliestHour }: WeeklyCalendarTableProps) => {
  const navigate = useNavigate()
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => dayjs(selectedDate).startOf('week').add(i, 'day').format('YYYY-MM-DD')),
    [selectedDate]
  )

  const hours = useMemo(
    () => Array.from({ length: 24 }, (_, i) => dayjs().hour(0).minute(0).add(i, 'hour').format('HH:00')),
    []
  )

  useEffect(() => {
    if (!tableContainerRef.current) return
    tableContainerRef.current.scrollTop = earliestHour >= 8 ? 350 : 350 - 45 * (8 - earliestHour)
  }, [earliestHour])

  const navigateToUser = (id: string) => {
    navigate(`/users/${id}`)
  }

  return (
    <TableContainer ref={tableContainerRef} sx={{ maxHeight: 500, overflowY: 'auto', border: 'none', width: '100%' }}>
      <Table sx={{ borderCollapse: 'separate', borderSpacing: '0px' }}>
        <WeeklyCalendarHeader selectedDate={selectedDate} />
        <TableBody>
          <TableRow sx={{ position: 'sticky', top: '34.29px', zIndex: 5 }}>
            <StyledStickyHourCell key={'gap'}></StyledStickyHourCell>
            {Array.from({ length: 7 }, (_, i) => {
              const day = weekDays[i]
              const isWeekend = dayjs(day).day() === 0 || dayjs(day).day() === 6
              return (
                <StyledStickyAvatarCell
                  key={i}
                  sx={{
                    backgroundColor: isWeekend ? 'background.elevation2' : 'background.elevation1',
                  }}
                >
                  <Stack
                    sx={{
                      width: '100%',
                      height: 'auto',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      gap: '2px',
                    }}
                  >
                    {mentees.map(mentee => {
                      return (
                        <Stack
                          onClick={() => navigateToUser(mentee.id)}
                          key={`${mentee.id}`}
                          sx={{
                            minWidth: '15px',
                            maxWidth: '30px',
                            width: '100%',
                            alignItems: 'center',
                            cursor: 'pointer',
                          }}
                        >
                          <Tooltip title={`${mentee.firstName} ${mentee.lastName}`}>
                            <AvatarComponent
                              alt={`${mentee.firstName} ${mentee.lastName}`}
                              size={18}
                              avatar={mentee.avatar}
                              extension={mentee.extension}
                            />
                          </Tooltip>
                        </Stack>
                      )
                    })}
                  </Stack>
                </StyledStickyAvatarCell>
              )
            })}
          </TableRow>
          {hours.map(hour => (
            <TableRow sx={{ height: '40px !important' }} key={hour}>
              <StyledHourCell key={'gap'}>
                <Box sx={{ marginTop: '-33px' }}>{hour !== '00:00' && hour}</Box>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: '92%',
                    width: '8%',
                    height: '1px',
                    backgroundColor: 'background.elevation4',
                  }}
                />
              </StyledHourCell>
              {Array.from({ length: 7 }, (_, i) => {
                const day = weekDays[i]
                const isWeekend = dayjs(day).day() === 0 || dayjs(day).day() === 6
                return (
                  <StyledLeaderTableCell
                    key={`${i}-${hour}`}
                    sx={{
                      backgroundColor: isWeekend ? 'background.elevation2' : '',
                    }}
                  >
                    <Stack
                      sx={{
                        width: '100%',
                        height: '100%',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        gap: '2px',
                      }}
                    >
                      {mentees.map(mentee => (
                        <WeeklyBar key={mentee.id} day={day} hour={hour} mentee={mentee} />
                      ))}
                    </Stack>
                  </StyledLeaderTableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}